import { lazy } from "react";
import type { DemoDefinition, DemoId } from "@/demos/types";

const loaders = {
  see: () => import("@/demos/see/SeeScene"),
  explore: () => import("@/demos/explore/ExploreScene"),
  engage: () => import("@/demos/engage/EngageScene"),
} as const;

const SeeScene = lazy(async () => ({ default: (await loaders.see()).SeeScene }));
const ExploreScene = lazy(async () => ({ default: (await loaders.explore()).ExploreScene }));
const EngageScene = lazy(async () => ({ default: (await loaders.engage()).EngageScene }));

export const demoRegistry: readonly DemoDefinition[] = [
  {
    id: "see",
    order: 0,
    title: "SEE",
    hint: "Tap the devices to reveal them",
    arFloorOffsetY: 0.08,
    // A closed laptop seen edge-on is a dark slab. Look down at the desk.
    viewerFraming: { position: [0, 2.9, 5.4], target: [0, -0.5, -3.0] },
    Scene: SeeScene,
    preload: async () => { await (await loaders.see()).preloadSeeAsset(); },
  },
  {
    id: "explore",
    order: 1,
    title: "EXPLORE",
    hint: "Tap a hotspot or change the finish",
    arFloorOffsetY: 0,
    viewerFraming: { position: [0, 0.55, 4.7], target: [0, 0.15, -3.4] },
    Scene: ExploreScene,
    preload: async () => { await (await loaders.explore()).preloadExploreAsset(); },
  },
  {
    id: "engage",
    order: 2,
    title: "ENGAGE",
    hint: "Tap to flip",
    arFloorOffsetY: 0.975,
    // Close and level: the card is the payoff, it should fill the frame.
    viewerFraming: { position: [0, 0.1, 2.6], target: [0, -0.5, -3.4] },
    Scene: EngageScene,
    preload: async () => { await loaders.engage(); },
  },
];

export const demosById: Readonly<Record<DemoId, DemoDefinition>> = Object.fromEntries(
  demoRegistry.map((demo) => [demo.id, demo]),
) as Record<DemoId, DemoDefinition>;

export function getDemo(id: DemoId): DemoDefinition {
  return demosById[id];
}

export function nextDemoId(id: DemoId): DemoId {
  return demoRegistry[(getDemo(id).order + 1) % demoRegistry.length]?.id ?? "see";
}

export function isLastDemo(id: DemoId): boolean {
  return getDemo(id).order === demoRegistry.length - 1;
}

export async function preloadDemo(id: DemoId): Promise<void> {
  await getDemo(id).preload();
}
